import { Box, Button, Container, Link, Stack, Typography, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router";
import { glassCardSx } from "src/theme/glassDialog";
import { TIETOSUOJA_ROUTE } from "src/utils/quote/quoteTerms";
import { useT, useTRich } from "src/i18n";

// GDPR / ePrivacy cookie-consent banner. Shown only when GA4/Ads tracking is
// configured and the visitor has not yet chosen. "Hyväksy" grants Consent Mode
// v2 ad+analytics storage; "Hylkää" keeps it denied (Google still runs in
// cookieless mode). Both buttons are equally prominent — rejecting must be as
// easy as accepting. Glass treatment matches the /tilaa wizard via
// theme.custom.wizard. The `consent` prop is App's shared useCookieConsent
// instance — the footer "Evästeasetukset" link reopens this same banner.
export default function CookieConsent({ consent }) {
    const theme = useTheme();
    const t = useT();
    const tRich = useTRich();
    const { showBanner, accept, reject } = consent;
    if (!showBanner) return null;

    return (
        <Box
            role="dialog"
            aria-label={t("landing.cookieConsent.ariaLabel")}
            sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: (t) => t.zIndex.snackbar,
                p: { xs: 1.5, sm: 2 },
                pointerEvents: "none",
            }}
        >
            <Container
                maxWidth="md"
                sx={{
                    pointerEvents: "auto",
                    p: { xs: 2, sm: 2.5 },
                    ...glassCardSx(theme),
                    // Fully rounded ends, on purpose — NOT the helper's `${cardRadius}px`
                    // (fb#501). Keep this a px STRING: a bare number is a MULTIPLIER of
                    // shape.borderRadius (6), which is how `cardRadius ?? 2` silently
                    // became 144px here while naming 24.
                    borderRadius: "999px",
                }}
            >
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 1.5, sm: 2.5 }}
                    sx={{ alignItems: { sm: "center" } }}
                >
                    <Typography variant="body2" sx={{ color: "text.primary", lineHeight: 1.5, flex: 1 }}>
                        <strong>{t("landing.cookieConsent.intro")}</strong>{" "}
                        {tRich("landing.cookieConsent.body", {
                            link: (
                                <Link component={RouterLink} to={TIETOSUOJA_ROUTE} underline="hover">
                                    {t("landing.cookieConsent.readMore")}
                                </Link>
                            ),
                        })}
                    </Typography>
                    <Stack
                        direction={{ xs: "column-reverse", sm: "row" }}
                        spacing={1.5}
                        sx={{ justifyContent: "flex-end", flexShrink: 0 }}
                    >
                        <Button variant="outlined" color="inherit" onClick={reject} fullWidth={false}>
                            {t("landing.cookieConsent.reject")}
                        </Button>
                        <Button variant="contained" color="warning" onClick={accept} fullWidth={false}>
                            {t("landing.cookieConsent.accept")}
                        </Button>
                    </Stack>
                </Stack>
            </Container>
        </Box>
    );
}
